import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { SmartyAuthService } from '../services/smarty-auth.service';
import { SsoNavigationService } from '../services/sso-navigation.service';
import { SKIP_AUTHENTICATION_REDIRECT } from '../tokens/auth-request-context.tokens';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  if (!isCwsUrl(request.url)) {
    return next(request);
  }

  const authService = inject(SmartyAuthService);
  const ssoNavigation = inject(SsoNavigationService);

  return next(buildAuthenticatedRequest(request, authService.getAccessToken())).pipe(
    catchError((error) =>
      isUnauthorized(error)
        ? refreshAndRetry(request, next, authService, ssoNavigation)
        : throwError(() => error),
    ),
  );
};

function isCwsUrl(value: string): boolean {
  const requestUrl = new URL(value, window.location.origin);
  const cwsUrl = new URL(environment.cws, window.location.origin);
  const basePath = cwsUrl.pathname.replace(/\/+$/, '');

  return (
    requestUrl.origin === cwsUrl.origin &&
    (requestUrl.pathname === basePath || requestUrl.pathname.startsWith(`${basePath}/`))
  );
}

function buildAuthenticatedRequest(
  request: HttpRequest<unknown>,
  token: string | null | undefined,
): HttpRequest<unknown> {
  const requestWithCredentials = request.clone({ withCredentials: true });
  return token
    ? requestWithCredentials.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      })
    : requestWithCredentials;
}

function refreshAndRetry(
  originalRequest: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: SmartyAuthService,
  ssoNavigation: SsoNavigationService,
) {
  const skipAuthenticationRedirect = originalRequest.context.get(SKIP_AUTHENTICATION_REDIRECT);

  return authService.refreshSession().pipe(
    catchError((error) => {
      if (!skipAuthenticationRedirect) {
        ssoNavigation.redirectToSso();
      }
      return throwError(() => error);
    }),
    switchMap((token) =>
      next(buildAuthenticatedRequest(originalRequest, token)).pipe(
        catchError((error) => {
          if (isUnauthorized(error) && !skipAuthenticationRedirect) {
            authService.clearAccessToken();
            ssoNavigation.redirectToSso();
          }
          return throwError(() => error);
        }),
      ),
    ),
  );
}

function isUnauthorized(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse && error.status === 401;
}
